import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LetDirective } from '@ngrx/component';
import { TranslateModule } from '@ngx-translate/core';
import { CmsFacade } from '@rd/cms/facades/cms.facade';
import { ButtonModule } from '@rd/shared-legacy/components/button/button.module';
import { IconModule } from '@rd/shared-legacy/components/icon/icon.module';
import { CmsPageContentType, MultipleCampaignBanner } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import {
  EmblaCarouselDirective,
  EmblaEventType,
  EmblaOptionsType,
} from 'embla-carousel-angular';
import Autoplay from 'embla-carousel-autoplay';
import { filter, map, Observable } from 'rxjs';
import { AnzHomePageBannerActionButtonComponent } from './homepage-banner-action-button.component';

@Component({
  selector: 'rd-anz-homepage-banner',
  template: `
    <div class="h-[198px] lg:h-[400px]">
      <div
        class="flex carousel-root overflow-hidden relative"
        emblaCarousel
        [options]="(emblaOptions$ | async) ?? {}"
        [plugins]="emblaPlugins"
        [subscribeToEvents]="subscribeToEvents"
        (emblaChange)="onEmblaChange()"
      >
        <div
          class="carousel-container relative flex h-[198px] lg:h-[400px]"
          [class.w-full]="(bannerContent$ | async)?.items?.length === 1"
        >
          <div
            class="carousel-items flex-[0_0_100%] overflow-hidden flex items-center"
            *ngFor="
              let bannerItem of (bannerContent$ | async)?.items;
              let i = index
            "
            [id]="i">
            <img
              class="hidden lg:block h-full object-center object-cover w-full"
              [src]="bannerItem.desktopImageUrl"
              [alt]="'Desktop banner image ' + i" />
            <img
              class="lg:hidden h-full object-center object-cover w-full"
              [src]="bannerItem.mobileImageUrl"
              [alt]="'Mobile banner image ' + i" />
          </div>
        </div>

        <div
          *ngIf="(bannerContent$ | async)?.items as items"
          class="absolute flex flex-col top-5 z-10 w-auto lg:top-[140px] lg:inset-x-12 text-[#004165]"
        >
          <div
            class="flex flex-col gap-y-6 mx-auto w-full max-w-[1024px]"
            *ngrxLet="items[scrollIndex()] as item"
          >
            <h1 [ngClass]="[
              'my-0 mx-5 pt-0 lg:w-[482px] lg:mx-0',
              'typo-heading-1-mobile lg:typo-heading-1-desktop',
            ]" [innerHTML]="getSafeHtml(item?.title)"></h1>
            <p class="my-0 mt-[-16px] lg:mt-0 mx-5 lg:mx-0 lg:w-[482px] font-bold lg:block text-[12px] lg:text-base" [innerHTML]="getSafeHtml(item?.mainContent)"></p>
            <rd-shared-button
              *ngIf="!!item.subContent.length"
              buttonTheme="primary"
              class="w-fit mx-5 [&_a]:!px-6 lg:mx-0"
              [redirectURL]="item.link"
              [buttonText]="item.subContent"
            />
          </div>
        </div>

        <ng-container *ngIf="(hasSingleBannerContent$ | async) === false">
          <div class="hidden items-center justify-between w-full h-full absolute top-0 lg:flex">
            <button
              anz-banner-action-button
              actionButtonType="left"
              (click)="onPrev()"
            >
              <rd-shared-icon
                class="h-4 w-4 inline opacity-75"
                iconSymbol="icon-chevron-left"
              />
            </button>

            <button
              anz-banner-action-button
              actionButtonType="right"
              (click)="onNext()"
            >
              <rd-shared-icon
                class="h-4 w-4 inline opacity-75"
                iconSymbol="icon-chevron-right"
              />
            </button>
          </div>

          <div [ngClass]="[
            'slides-indicator absolute bottom-4 inset-x-0 z-10',
            'flex flex-row items-center justify-center gap-x-1'
          ]">
            <div
              *ngFor="let item of (bannerContent$ | async)?.items; index as index; trackBy: trackByFn"
              [ngClass]="[
                'w-2 h-2 rounded-full hover:cursor-pointer',
                'transition-all duration-300 ease-in',
                index === scrollIndex() ? '!w-4 rounded bg-white' : 'bg-[#cccccc]',
              ]"
              (click)="onSlidesIndicatorClicked(index)">
            </div>
          </div>
        </ng-container>
      </div>
    </div>
  `,
  imports: [
    CommonModule,
    LetDirective,
    TranslateModule,
    IconModule,
    ButtonModule,
    EmblaCarouselDirective,
    AnzHomePageBannerActionButtonComponent,
  ],
})
export class AnzHomePageBannerComponent {
  @ViewChild(EmblaCarouselDirective) emblaRef: EmblaCarouselDirective;

  public readonly subscribeToEvents: EmblaEventType[] = ['select'];

  private cmsFacade = inject(CmsFacade);
  private sanitizer = inject(DomSanitizer);
  private cdRef = inject(ChangeDetectorRef);

  bannerContent$: Observable<MultipleCampaignBanner> = this.cmsFacade
    .getContentByName({
      pageName: 'homepage-banner',
      pageType: 'campaignbannermultiple',
      locale: 'en-AU',
    })
    .pipe(
      filter(isNotNull),
      map((data: CmsPageContentType) => data as MultipleCampaignBanner),
    );

  emblaOptions$: Observable<EmblaOptionsType> = this.bannerContent$.pipe(
    map((data: MultipleCampaignBanner) => {
      const emblaOptions: EmblaOptionsType = {
        watchDrag: data.items.length !== 1,
        dragThreshold: 300,
        loop: true,
      } as EmblaOptionsType;

      return emblaOptions;
    }),
    filter(isNotNull),
  );
  emblaPlugins = [
    Autoplay({
      delay: 15_000,
      stopOnInteraction: false,
    }),
  ];

  hasSingleBannerContent$: Observable<boolean> = this.bannerContent$.pipe(
    map(({ items }: MultipleCampaignBanner) => items.length === 1),
  );

  /**
   * Read the live instance on every call. embla-carousel-angular >= 21 creates
   * `emblaApi` in `afterNextRender`, i.e. after this component's
   * `ngAfterViewInit`, so caching it there captured `undefined` for good.
   */
  scrollIndex() {
    return this.emblaRef?.emblaApi?.selectedScrollSnap() ?? 0;
  }

  /**
   * Bypass Angular's sanitization to allow inline styles in CMS content
   */
  getSafeHtml(html: string | undefined): SafeHtml {
    return html ? this.sanitizer.bypassSecurityTrustHtml(html) : '';
  }

  nextSlideTitle(campaignBanner: MultipleCampaignBanner['items']) {
    return campaignBanner?.[this.scrollIndex()]?.subContent;
  }

  onEmblaChange() {
    this.cdRef.markForCheck();
  }

  onPrev() {
    this.emblaRef.scrollPrev();
  }

  onNext() {
    this.emblaRef.scrollNext();
  }

  onSlidesIndicatorClicked(index: number) {
    this.emblaRef.scrollTo(index);
  }

  trackByFn(
    index: number,
    { title }: MultipleCampaignBanner['items'][0],
  ): string {
    return `${title}+${index}`;
  }
}
